import { MaterialCommunityIcons, MaterialIcons } from "@expo/vector-icons";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { LinearGradient } from "expo-linear-gradient";
import { Box, Center, HStack, Icon, Image, Input, ScrollView, Spinner, Text, useTheme, VStack } from "native-base";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Animated, FlatList, Pressable, StyleSheet, Text as RNText, View } from "react-native";
import { useSelector } from "react-redux";

import AnimatedRecipeCarousel from "../components/AnimatedRecipeCarousel";
import CategoryCard from "../components/CategoryCard";
import ScreenContainer from "../components/ScreenContainer";
import SimpleRecipeInspirationSkeleton from "../components/SimpleRecipeInspirationSkeleton";
import { commonStyles, Routes, Scale, VerticalScale } from "../constants";
import { getSimpleRecipeImage } from "../helpers/generalHelpers";
import { getTotalRecipeTime } from "../helpers/simpleRecipeHelpers";
import { getOrganisation } from "../helpers/userHelpers";
import type { RootStackParamList } from "../navigation/NavigationStackParams";
import backendApi from "../services/backendApi";
import type {
  MealSlotSpecification,
  SimpleRecipe,
  SimpleRecipeCategoryCard,
  SimpleRecipeCustomTag,
  SimpleRecipeInspiration,
  SimpleRecipeMeal,
} from "../services/backendTypes";
import logger from "../services/logger";
import { userSelector } from "../slices/userSlice";

const { useFoodSimpleRecipeInspirationRetrieveQuery, useFoodSimpleRecipeListQuery } = backendApi;

// Create a dummy MealSlotSpecification as SimpleRecipeDetailScreen requires it
const dummyMealSlotSpecification: MealSlotSpecification = {
  id: -1,
  meal_type: "SNACK",
  kcal: 0,
  protein: 0,
  fat: 0,
  carbohydrates: 0,
  order: 0,
  meal_moment: "BREAKFAST",
  nutrition_day_plan: -1,
};

// Helper function to convert SimpleRecipe to SimpleRecipeMeal format
const convertToSimpleRecipeMeal = (simpleRecipe: SimpleRecipe): SimpleRecipeMeal => ({
  id: simpleRecipe.id,
  simple_recipe: simpleRecipe,
  meal_servings: simpleRecipe.servings || 1,
  total_calories: simpleRecipe.total_calories,
  total_protein: simpleRecipe.protein_grams,
  total_carbohydrates: simpleRecipe.carbohydrates_grams,
  total_fat: simpleRecipe.fat_grams,
  total_fiber: simpleRecipe.fiber_grams || null,
});

type Props = {
  navigation: NativeStackNavigationProp<RootStackParamList, Routes.SimpleRecipeInspirationScreen>;
};

const SimpleRecipeInspirationScreen = ({ navigation }: Props): JSX.Element => {
  const { t: screenT } = useTranslation();
  const theme = useTheme();
  const user = useSelector(userSelector);

  const [whatsNewActiveIndex, setWhatsNewActiveIndex] = useState(0);

  // Use the new inspiration endpoint
  const { data, error, isLoading } = useFoodSimpleRecipeInspirationRetrieveQuery();

  // Fetch user's favorite recipes
  const { data: favoritesData, isLoading: isFavoritesLoading } = useFoodSimpleRecipeListQuery({
    isFavorite: true,
    page: 1,
    pageSize: 10, // Show up to 10 favorites in horizontal scroll
  });

  // Type the featured section properly since it's `any` in the generated types
  const featuredSection = data?.featured_section as { title: string; recipes: SimpleRecipe[] } | undefined;

  const onRecipePress = (simpleRecipe: SimpleRecipe): void => {
    navigation.push(Routes.SimpleRecipeDetailScreen, {
      simpleRecipeMeal: convertToSimpleRecipeMeal(simpleRecipe),
      mealSlotSpecification: dummyMealSlotSpecification,
      currentCalendarDay: {
        id: 0,
        user: user?.id || 0,
        date: new Date().toISOString().split("T")[0]!,
        calendar_items: [],
      },
      onSelect: async (): Promise<void> => {
        // This is for viewing only, no selection action needed
      },
      viewOnly: true,
    });
  };

  const onSearchBarPress = (): void => {
    navigation.push(Routes.SimpleRecipeSearchScreen, {
      mealSlotSpecification: dummyMealSlotSpecification,
      currentCalendarDay: {
        id: 0,
        user: user?.id || 0,
        date: new Date().toISOString().split("T")[0]!,
        calendar_items: [],
      },
    });
  };

  // Render function for category cards
  const renderCategoryCard = ({ item, index }: { item: SimpleRecipeCategoryCard; index: number }): JSX.Element => (
    <CategoryCard
      name={item.name}
      title={item.title}
      recipe={item.first_recipe}
      onPress={() => {
        navigation.push(Routes.SimpleRecipeSearchScreen, {
          mealSlotSpecification: dummyMealSlotSpecification,
          currentCalendarDay: {
            id: 0,
            user: user?.id || 0,
            date: new Date().toISOString().split("T")[0]!,
            calendar_items: [],
          },
          customTags: [item.tag_id],
          hideSearchBar: true,
          tagTitle: item.title,
        });
      }}
      index={index}
    />
  );

  // Render function for horizontal section recipe cards
  const renderSectionRecipeCard = ({ item }: { item: SimpleRecipe }): JSX.Element => {
    const simpleRecipeMeal = convertToSimpleRecipeMeal(item);

    return (
      <Pressable onPress={() => onRecipePress(item)}>
        <Box
          width={Scale(172)}
          height={Scale(172)}
          overflow="hidden"
          position="relative"
          mr={Scale(8)}
          borderRadius={theme.borderRadii.card.default}
          shadow="10"
        >
          <Image
            source={getSimpleRecipeImage(simpleRecipeMeal).source}
            alt={item.name}
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            width="100%"
            height="100%"
            resizeMode="cover"
          />
          <LinearGradient
            colors={["transparent", `${theme.colors.function[50]}CC`, theme.colors.function[50]]}
            locations={[0.2, 0.6, 1]}
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: "85%",
            }}
          />
          <VStack position="absolute" bottom={0} left={0} right={0} px={3} pb={3} space={1}>
            <Text
              fontSize="xl"
              fontFamily={theme.fonts.serif}
              fontStyle="italic"
              fontWeight="400"
              color="function.10"
              numberOfLines={2}
            >
              {item.name}
            </Text>
            <Text fontSize="sm" fontFamily={theme.fonts.heading} color="function.10">
              {getTotalRecipeTime(item)} mins
            </Text>
          </VStack>
        </Box>
      </Pressable>
    );
  };

  // Helper function to select the best tag to display for a recipe in a vertical section
  const selectDisplayTag = (
    recipe: SimpleRecipe,
    sectionName: string,
    recipeIndex: number,
    allRecipesInSection: SimpleRecipe[]
  ): SimpleRecipeCustomTag | undefined => {
    if (!recipe.custom_tags || recipe.custom_tags.length === 0) {
      return undefined;
    }

    // Try to find the section's main tag from category cards
    const sectionMainTag = data?.category_cards?.find((card) => card.name === sectionName)?.title?.toLowerCase();

    // Create a priority list of tags to display
    // First, add tags that are different from the section's main tag
    const differentTags = recipe.custom_tags.filter(
      (tag) => !sectionMainTag || !tag.human_name.toLowerCase().includes(sectionMainTag)
    );

    // Then add remaining tags as fallback
    const sameTags = recipe.custom_tags.filter(
      (tag) => sectionMainTag && tag.human_name.toLowerCase().includes(sectionMainTag)
    );

    const tagPriority = [...differentTags, ...sameTags];

    // Try to select a tag that hasn't been used by other recipes in this section
    const usedTags = new Set<string>();
    allRecipesInSection.forEach((r, idx) => {
      if (idx < recipeIndex && r.custom_tags && r.custom_tags.length > 0) {
        // For already rendered recipes, track what tag would be displayed
        const wouldDisplay =
          r.custom_tags.find((t) => !sectionMainTag || !t.human_name.toLowerCase().includes(sectionMainTag)) ||
          r.custom_tags[0];
        if (wouldDisplay) {
          usedTags.add(wouldDisplay.name);
        }
      }
    });

    // Find the first tag that hasn't been used yet
    const unusedTag = tagPriority.find((tag) => !usedTags.has(tag.name));

    // Return the unused tag if found, otherwise return the first priority tag
    return unusedTag || tagPriority[0];
  };

  // Render function for vertical column recipe cards
  const renderVerticalRecipeCard = (
    recipe: SimpleRecipe,
    sectionIndex: number,
    sectionName: string,
    allRecipesInSection: SimpleRecipe[],
    recipeIndex: number
  ): JSX.Element => {
    const simpleRecipeMeal = convertToSimpleRecipeMeal(recipe);
    const displayTag = selectDisplayTag(recipe, sectionName, recipeIndex, allRecipesInSection);

    return (
      <Pressable onPress={() => onRecipePress(recipe)} key={`vertical-${sectionIndex}-${recipe.id}`}>
        <HStack width="100%" borderRadius={theme.borderRadii.card.default} mb={3} alignItems="center" space={3}>
          {/* Square recipe image */}
          <Box width={Scale(100)} height={Scale(100)} borderRadius={theme.borderRadii.card.small} overflow="hidden">
            <Image
              source={getSimpleRecipeImage(simpleRecipeMeal).source}
              alt={recipe.name}
              width="100%"
              height="100%"
              resizeMode="cover"
            />
          </Box>

          {/* Recipe name and tag */}
          <VStack flex={1} space={1}>
            <Text
              fontSize="md"
              fontFamily={theme.fonts.serif}
              fontStyle="italic"
              fontWeight="400"
              color="function.50"
              numberOfLines={2}
            >
              {recipe.name}
            </Text>
            {displayTag ? (
              <Text fontSize="sm" fontFamily={theme.fonts.body} color="function.40" numberOfLines={1}>
                {displayTag.human_name}
              </Text>
            ) : null}
          </VStack>

          {/* Arrow icon */}
          <Icon as={MaterialIcons} name="chevron-right" size="lg" color="function.30" />
        </HStack>
      </Pressable>
    );
  };

  // Render function for animated carousel
  const renderAnimatedRecipeCard = (item: SimpleRecipe, scale: Animated.AnimatedInterpolation<number>): JSX.Element => {
    const simpleRecipeMeal = convertToSimpleRecipeMeal(item);

    return (
      <Pressable onPress={() => onRecipePress(item)}>
        <Box
          width="172px"
          height="172px"
          overflow="hidden"
          position="relative"
          borderRadius={theme.borderRadii.card.default}
          shadow="10"
        >
          <Image
            source={getSimpleRecipeImage(simpleRecipeMeal).source}
            alt={item.name}
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            width="100%"
            height="100%"
            resizeMode="cover"
          />
          <LinearGradient
            colors={["transparent", `${theme.colors.function[50]}CC`, theme.colors.function[50]]}
            locations={[0.2, 0.6, 1]}
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: "85%",
            }}
          />
          <VStack position="absolute" bottom={0} left={0} right={0} px={2} pb={1} space={0}>
            <Text
              fontSize="sm"
              fontFamily={theme.fonts.serif}
              fontStyle="italic"
              fontWeight="400"
              color="function.10"
              numberOfLines={2}
              lineHeight="sm"
            >
              {item.name}
            </Text>
            <Text fontSize="10px" fontFamily={theme.fonts.heading} color="function.10">
              {getTotalRecipeTime(item)} mins
            </Text>
          </VStack>
        </Box>
      </Pressable>
    );
  };

  const headerComponent = useMemo(() => {
    const organisation = user ? getOrganisation(user) : undefined;
    const logoUri = organisation?.logo;

    return (
      <View>
        <VStack alignItems="center" p={4} space={4}>
          {Boolean(logoUri) && (
            <Image
              source={{ uri: logoUri }}
              style={{ width: Scale(160), height: VerticalScale(60) }}
              resizeMode="contain"
              alt="Organization Logo"
            />
          )}
          {!logoUri && <Icon as={MaterialCommunityIcons} name="robot" size="16" color="primary.50" />}
        </VStack>
      </View>
    );
  }, [user]);

  if (error) {
    logger.error("Error fetching simple recipe inspiration:", error);
  }

  const renderContent = (): JSX.Element => {
    if (isLoading) {
      return <SimpleRecipeInspirationSkeleton />;
    }

    if (error) {
      return (
        <Center flex={1}>
          <VStack space={2} alignItems="center">
            <Icon as={MaterialIcons} name="error-outline" size="lg" color="error.500" />
            <RNText style={[styles.errorText, { color: theme.colors.error[500] }]}>
              {screenT("general.error_loading_data")}
            </RNText>
          </VStack>
        </Center>
      );
    }

    if (!data) {
      return (
        <Center flex={1}>
          <Text color="function.50">{screenT("general.no_search_results")}</Text>
        </Center>
      );
    }

    return (
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ flexGrow: 1, paddingBottom: 20 }}
        keyboardShouldPersistTaps="handled"
      >
        {headerComponent}

        {/* Featured Section */}
        {Boolean(featuredSection && featuredSection.recipes.length > 0) && (
          <View style={{ marginBottom: Scale(24) }}>
            <Text
              fontSize="3xl"
              fontFamily="serif"
              fontStyle="italic"
              color="function.50"
              textAlign="center"
              lineHeight="28px"
              mb={-2}
            >
              {featuredSection?.title ?? ""}
            </Text>

            <Box height="300px" justifyContent="center">
              <AnimatedRecipeCarousel
                data={featuredSection?.recipes ?? []}
                renderItem={renderAnimatedRecipeCard}
                onActiveIndexChange={setWhatsNewActiveIndex}
              />
            </Box>

            <HStack justifyContent="center" alignItems="center" space={2} mt={4}>
              {(featuredSection?.recipes ?? []).map((_recipe: SimpleRecipe, index: number) => (
                <Box
                  key={index}
                  w={index === whatsNewActiveIndex ? "12px" : "10px"}
                  h={index === whatsNewActiveIndex ? "12px" : "10px"}
                  borderRadius="full"
                  bg={index === whatsNewActiveIndex ? "primary.30" : "primary.20"}
                />
              ))}
            </HStack>
          </View>
        )}

        {/* Search Bar */}
        <Box px={4} mb={8} mt={4}>
          <Pressable onPress={onSearchBarPress}>
            <Box pointerEvents="none">
              <Input
                placeholder={screenT("recipe_search.search_prompt")}
                InputLeftElement={<Icon as={MaterialIcons} mx="2" size="md" name="search" color="function.30" />}
                testID="simple-recipe-search-input-inspiration"
                borderRadius={theme.borderRadii.input.default}
                bg="primary.10"
                borderColor="function.30"
                borderWidth={1}
                height={12}
                fontSize="md"
                fontFamily={theme.fonts.body}
                placeholderTextColor="function.30"
                color="function.50"
                editable={false}
                _focus={{
                  borderColor: "function.30",
                  bg: "primary.10",
                }}
                _web={{
                  _focus: {
                    outlineStyle: "none",
                    shadow: "none",
                  },
                }}
              />
            </Box>
          </Pressable>
        </Box>

        {/* Category Cards */}
        {Boolean(data.category_cards && data.category_cards.length > 0) && (
          <View style={{ marginBottom: Scale(24) }}>
            <Text fontSize="3xl" fontFamily="serif" fontStyle="italic" color="function.50" mb={1} ml={Scale(16)}>
              {screenT("recipe_inspiration.categories_label")}
            </Text>

            <FlatList
              horizontal
              data={data.category_cards ?? []}
              renderItem={renderCategoryCard}
              keyExtractor={(item) => `category-${item.name}`}
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{
                paddingHorizontal: Scale(16),
              }}
              testID="categories-horizontal-list"
            />
          </View>
        )}

        {/* Saved Recipes Section */}
        {Boolean(favoritesData?.results && favoritesData.results.length > 0) && (
          <View style={{ marginBottom: Scale(24) }}>
            <View style={commonStyles.paddingContainer}>
              <HStack justifyContent="space-between" alignItems="center" mb={2}>
                <Text fontSize="3xl" fontFamily="serif" fontStyle="italic" color="function.50">
                  {screenT("recipe_inspiration.saved_recipes")}
                </Text>
                <Pressable
                  onPress={() => {
                    navigation.push(Routes.SimpleRecipeSearchScreen, {
                      mealSlotSpecification: dummyMealSlotSpecification,
                      currentCalendarDay: {
                        id: 0,
                        user: user?.id || 0,
                        date: new Date().toISOString().split("T")[0]!,
                        calendar_items: [],
                      },
                      showFavorites: true,
                    });
                  }}
                  style={({ pressed }) => ({
                    opacity: pressed ? 0.7 : 1,
                  })}
                >
                  <Text fontSize="md" fontFamily="body" color="function.50">
                    {screenT("recipe_inspiration.see_all")}
                  </Text>
                </Pressable>
              </HStack>
            </View>

            <FlatList
              horizontal
              data={favoritesData?.results ?? []}
              renderItem={renderSectionRecipeCard}
              keyExtractor={(item) => `saved-${item.id}`}
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{
                paddingHorizontal: Scale(16),
              }}
              testID="saved-recipes-horizontal-list"
            />
          </View>
        )}

        {/* Curated Sections */}
        {(data.curated_sections ?? []).map((section, sectionIndex) => {
          if (section.layout === "horizontal" && section.recipes.length > 0) {
            return (
              <View key={section.id} style={{ marginBottom: Scale(24) }}>
                <View style={commonStyles.paddingContainer}>
                  <HStack justifyContent="space-between" alignItems="center" mb={2}>
                    <Text fontSize="3xl" fontFamily="serif" fontStyle="italic" color="function.50">
                      {section.title}
                    </Text>
                    <Pressable
                      onPress={() => {
                        const matchingCard = (data.category_cards ?? []).find((card) => card.name === section.id);
                        if (matchingCard) {
                          navigation.push(Routes.SimpleRecipeSearchScreen, {
                            mealSlotSpecification: dummyMealSlotSpecification,
                            currentCalendarDay: {
                              id: 0,
                              user: user?.id || 0,
                              date: new Date().toISOString().split("T")[0]!,
                              calendar_items: [],
                            },
                            customTags: [matchingCard.tag_id],
                            hideSearchBar: true,
                            tagTitle: matchingCard.title,
                          });
                        }
                      }}
                      style={({ pressed }) => ({
                        opacity: pressed ? 0.7 : 1,
                      })}
                    >
                      <Text fontSize="md" fontFamily="body" color="function.50">
                        {screenT("recipe_inspiration.see_all")}
                      </Text>
                    </Pressable>
                  </HStack>
                </View>

                <FlatList
                  horizontal
                  data={section.recipes}
                  renderItem={renderSectionRecipeCard}
                  keyExtractor={(item) => `${section.id}-${item.id}`}
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={{
                    paddingHorizontal: Scale(16),
                  }}
                  testID={`${section.id}-horizontal-list`}
                />
              </View>
            );
          }

          if (section.layout === "vertical" && section.recipes.length > 0) {
            return (
              <View key={section.id} style={{ marginBottom: Scale(24) }}>
                <View style={commonStyles.paddingContainer}>
                  <HStack justifyContent="space-between" alignItems="center" mb={2}>
                    <Text fontSize="3xl" fontFamily="serif" fontStyle="italic" color="function.50">
                      {section.title}
                    </Text>
                    <Pressable
                      onPress={() => {
                        const matchingCard = (data.category_cards ?? []).find((card) => card.name === section.id);
                        if (matchingCard) {
                          navigation.push(Routes.SimpleRecipeSearchScreen, {
                            mealSlotSpecification: dummyMealSlotSpecification,
                            currentCalendarDay: {
                              id: 0,
                              user: user?.id || 0,
                              date: new Date().toISOString().split("T")[0]!,
                              calendar_items: [],
                            },
                            customTags: [matchingCard.tag_id],
                            hideSearchBar: true,
                            tagTitle: matchingCard.title,
                          });
                        }
                      }}
                      style={({ pressed }) => ({
                        opacity: pressed ? 0.7 : 1,
                      })}
                    >
                      <Text fontSize="md" fontFamily="body" color="function.50">
                        {screenT("recipe_inspiration.see_all")}
                      </Text>
                    </Pressable>
                  </HStack>
                </View>

                <View style={commonStyles.paddingContainer}>
                  {(section.recipes ?? []).map((recipe: SimpleRecipe, recipeIndex: number) =>
                    renderVerticalRecipeCard(recipe, sectionIndex, section.id, section.recipes, recipeIndex)
                  )}
                </View>
              </View>
            );
          }

          return null;
        })}
      </ScrollView>
    );
  };

  return (
    <ScreenContainer nativeID="simpleRecipeInspirationScreen" bg="primary.10" safeAreaMode="top-horizontal">
      {renderContent()}
    </ScreenContainer>
  );
};

const styles = StyleSheet.create({
  errorText: {
    textAlign: "center",
    marginTop: Scale(5),
  },
});

export default SimpleRecipeInspirationScreen;
